/**
 * Canonical links to platform content (PRTL-981).
 *
 * An AI answer, a saved search, a shared email and both apps all need to
 * point at "that vendor" or "that article" without agreeing on a route. So
 * the stored form is a scheme, not a path: `article:123`, `blog:some-slug`,
 * `vendor:12`, `post:45`, `event:7` — the same shape as `mailto:`. Each
 * client maps a parsed ref to its own route; the server maps it to an
 * absolute web URL for email. Routes can move without touching stored data.
 *
 * The model is told to cite in exactly this shape (see the prompt rules
 * built from `CONTENT_LINK_PROMPT_RULES`), and `normalizeAnswerReferences`
 * repairs anything it gets wrong.
 */

export type ContentRef =
    | { type: "feedArticle"; id: number }
    | { type: "blogPost"; slug: string }
    | { type: "vendor"; id: number }
    | { type: "post"; id: number }
    | { type: "event"; id: number };

export type ContentRefType = ContentRef["type"];

/** URI scheme per content type. Lower-case, letters only, so parsers agree. */
export const CONTENT_LINK_SCHEME: Record<ContentRefType, string> = {
    feedArticle: "article",
    blogPost: "blog",
    vendor: "vendor",
    post: "post",
    event: "event",
};

/** Every scheme we own — register these wherever link hrefs are validated. */
export const CONTENT_LINK_SCHEMES: readonly string[] = Object.values(CONTENT_LINK_SCHEME);

const TYPE_BY_SCHEME: Record<string, ContentRefType> = Object.fromEntries(
    (Object.entries(CONTENT_LINK_SCHEME) as Array<[ContentRefType, string]>).map(([t, s]) => [
        s,
        t,
    ]),
);

const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/i;

export function contentLinkHref(ref: ContentRef): string {
    switch (ref.type) {
        case "blogPost":
            return `${CONTENT_LINK_SCHEME.blogPost}:${ref.slug}`;
        default:
            return `${CONTENT_LINK_SCHEME[ref.type]}:${ref.id}`;
    }
}

/** `article:123` → ref; anything else (including our scheme with a bad id) → null. */
export function parseContentLinkHref(href: string): ContentRef | null {
    const colon = href.indexOf(":");
    if (colon <= 0) return null;
    const scheme = href.slice(0, colon).toLowerCase();
    const type = TYPE_BY_SCHEME[scheme];
    if (!type) return null;
    const value = href.slice(colon + 1).trim();
    if (!value) return null;

    if (type === "blogPost") {
        return SLUG_RE.test(value) ? { type, slug: value } : null;
    }
    if (!/^\d+$/.test(value)) return null;
    const id = Number(value);
    return Number.isSafeInteger(id) && id > 0 ? { type, id } : null;
}

/** True for any href in one of our schemes, well-formed or not. */
export function isContentLinkScheme(href: string): boolean {
    const colon = href.indexOf(":");
    return colon > 0 && href.slice(0, colon).toLowerCase() in TYPE_BY_SCHEME;
}

/**
 * The web portal's route for a ref. Web renders this directly; the server
 * prefixes it with the web origin for email. Mobile has its own table in
 * `NavigatorLink` because its routes differ (e.g. blog posts).
 */
export function contentRefWebPath(ref: ContentRef): string {
    switch (ref.type) {
        case "feedArticle":
            return `/rss-feeds/article/${ref.id}`;
        case "blogPost":
            return `/p/${ref.slug}`;
        case "vendor":
            return `/vendors/${ref.id}`;
        case "post":
            return `/social/post/${ref.id}`;
        case "event":
            return `/events/${ref.id}`;
    }
}

/**
 * Legacy path-shaped links the model used to be told to emit. Recognised so
 * older prompts, cached answers and stray model output still resolve.
 */
export function contentRefFromLegacyPath(path: string): ContentRef | null {
    const m =
        /^\/rss-feeds\/article\/(\d+)\/?$/.exec(path) ??
        /^\/vendors\/(\d+)\/?$/.exec(path) ??
        /^\/social\/post\/(\d+)\/?$/.exec(path) ??
        /^\/events\/(\d+)\/?$/.exec(path);
    if (m) {
        const id = Number(m[1]);
        if (!Number.isSafeInteger(id) || id <= 0) return null;
        if (path.startsWith("/rss-feeds/")) return { type: "feedArticle", id };
        if (path.startsWith("/vendors/")) return { type: "vendor", id };
        if (path.startsWith("/social/")) return { type: "post", id };
        return { type: "event", id };
    }
    const blog = /^\/(?:p|articles\/post|spotlight\/post|multimedia\/post)\/([a-z0-9-]+)\/?$/i.exec(
        path,
    );
    return blog ? { type: "blogPost", slug: blog[1] } : null;
}

/**
 * The ref behind any href a renderer meets: our scheme first, then a legacy
 * path. Saved searches and recent-search history written before PRTL-981
 * still hold path links (`/p/slug`, `/rss-feeds/article/123`), and each
 * app's routes differ, so every renderer resolves through this rather than
 * pushing the stored path as-is. Null means "not platform content".
 */
export function contentRefFromHref(href: string): ContentRef | null {
    return (
        parseContentLinkHref(href) ?? (href.startsWith("/") ? contentRefFromLegacyPath(href) : null)
    );
}

/** Prompt text: the exact citation shape per content type. One source for all prompts. */
export const CONTENT_LINK_PROMPT_RULES = [
    `- Feed articles: [Article Title](${CONTENT_LINK_SCHEME.feedArticle}:{id})`,
    `- Blog posts: [Blog Post Title](${CONTENT_LINK_SCHEME.blogPost}:{slug})`,
    `- Vendors: [Vendor Name](${CONTENT_LINK_SCHEME.vendor}:{id})`,
    `- Posts: [Post Title](${CONTENT_LINK_SCHEME.post}:{id})`,
    `- Events: [Event Title](${CONTENT_LINK_SCHEME.event}:{id})`,
    "- Comments: link the parent post or vendor instead",
    "- Use the id or slug exactly as given in PLATFORM DATA; never write a bare id like [1234567] on its own — always the [Title](scheme:id) form",
].join("\n");
